import { useState, useRef, useEffect } from 'react';
import { message, Space } from 'antd';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { calculatePackagePrice } from 'services/api/pay';
import { isUndefined } from 'lodash-es';
import { PackageSelectPanel } from './PackageSelectPanel';
import { DurationSelectPanel } from './DurationSelectPanel';
import { PanelTitle } from './Common';

const noAddTime: Pay.DurationDescV2 = {
  id: 0,
  name: '0',
  price: 0,
  durationNum: 0,
  currencyAmount: [],
};

/** 连续包月（自动续费订阅）时长项的 id */
const SUBSCRIPTION_DURATION_ID = 5 as Pay.DurationId;

export interface PackageDetailProps {
  orderType: Pay.OrderType;
  orgId: number;
  currency: General.Currency;
  orderedPackages: Pay.OrderedPackage[];
  packageInfo: Pay.PackageInfoV2;
  checkedPackageId?: Pay.PackageId;
  curWorkingPackage?: Pay.OrderedPackage;
  setAccountNum: React.Dispatch<React.SetStateAction<number>>;
  setCanOrder: React.Dispatch<React.SetStateAction<boolean>>;
  isEnterprise: boolean;
  setIsEnterprise: React.Dispatch<React.SetStateAction<boolean>>;
  onPackagePriceChange?: (value?: Pay.CalculatePackagePriceRst) => void;
  onPackageChange?: (value: Pay.PackageId | undefined) => void;
  onDurationChange?: (value: Pay.DurationNum | -1) => void;
  setIsSubscription?: React.Dispatch<React.SetStateAction<boolean>>;
}

// 升级/续订套餐
function PackageDlgDetail(props: PackageDetailProps) {
  const {
    packageInfo,
    setAccountNum,
    checkedPackageId,
    orderedPackages,
    orgId,
    currency,
    orderType,
    setCanOrder,
    isEnterprise,
    setIsEnterprise,
    curWorkingPackage,
    onPackagePriceChange,
    onPackageChange,
    onDurationChange,
    setIsSubscription,
  } = props;

  const { t } = useTranslate();

  const subscriptionItem: Pay.DurationDescV2 = {
    id: SUBSCRIPTION_DURATION_ID,
    name: t('连续包月'),
    durationNum: -1,
    subscription: true,
    price: 0,
    currencyAmount: [],
  };

  // 当前用户是否为订阅用户（连续包月）
  const isWorkingSubscription = curWorkingPackage?.autoRenewing === true;
  // 订阅用户升级只选目标套餐，不显示时长选项
  const hiddenDuration = orderType === 'UPGRADE' && isWorkingSubscription;

  // 将「连续包月」选项插入到第一个时长项之后（升级默认首位是不增加时长）；
  // 非订阅用户升级不提供切订阅入口，不注入连续包月；订阅用户升级虽隐藏时长面板，但仍注入以按订阅时长计算
  const injectSubscriptionItem = (desc: Pay.DurationDescV2[]) => {
    if (!desc.length) return desc;
    if (orderType === 'UPGRADE' && !isWorkingSubscription) {
      return desc;
    }
    return [desc[0], subscriptionItem, ...desc.slice(1)];
  };

  const [durationDesc, setDurationDesc] = useState<Pay.PackageV2['durationDesc']>([]);

  const { packageData, recommendData } = packageInfo;
  const { recommendPackageId, recommendDurationId } = recommendData;
  // 升级：订阅用户时长固定为连续包月；非订阅用户默认不增加时长。
  // 续订：CNY 下默认连续包月，其他币种默认推荐时长
  const [chkDuration, setChkDuration] = useState(
    orderType === 'UPGRADE'
      ? isWorkingSubscription
        ? SUBSCRIPTION_DURATION_ID
        : 0
      : currency === 'CNY'
      ? SUBSCRIPTION_DURATION_ID
      : recommendDurationId,
  );
  const chkExtraDurationNum = useRef(0);

  const curRenewPackage = orderedPackages.find((v) => v.status === 0);

  // 连续包月仅支持 Stripe，而 Stripe 仅支持 CNY：
  // 货币切离 CNY 时退出订阅并复位到推荐时长（保持用户选择，不擅自切换其他状态；升级始终为不增加时长，不受影响）
  useEffect(() => {
    if (orderType === 'UPGRADE') {
      return;
    }
    if (currency !== 'CNY' && chkDuration === SUBSCRIPTION_DURATION_ID) {
      setChkDuration(recommendDurationId);
      setIsSubscription?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  useEffect(() => {
    if (!curWorkingPackage) {
      return;
    }
    packageData.forEach((account) => {
      const { id } = account;
      let duDesc = account.durationDesc;
      const checked = id === checkedPackageId;

      if (checked) {
        // 升级/续订均支持选择自动续费（连续包月），升级首位默认不增加时长
        duDesc = injectSubscriptionItem(orderType === 'UPGRADE' ? [noAddTime, ...duDesc] : duDesc);
        setDurationDesc(duDesc);
        duDesc.some((duration) => {
          const { id, durationNum, extraDurationNum } = duration;
          const checked = id === chkDuration;

          if (checked && checkedPackageId) {
            chkExtraDurationNum.current = extraDurationNum ?? 0;
            calculatePackagePrice({
              orgId,
              orderType,
              durationNum,
              newPackageId: checkedPackageId,
              currency,
            })
              .then((d) => {
                onDurationChange?.(durationNum);
                setCanOrder(true);
                onPackagePriceChange?.(d);
              })
              .catch((e) => {
                message.error(`/calculatePackagePrice ${e?.message}`);
                setCanOrder(false);
                onPackagePriceChange?.(undefined);
              });
          }
          return checked;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    packageData,
    chkDuration,
    checkedPackageId,
    orgId,
    curWorkingPackage,
    orderType,
    currency,
    setCanOrder,
  ]);

  return curWorkingPackage ? (
    <Space size={16} direction="vertical" className="w-full">
      <PanelTitle>
        {t('market.当前套餐')}
        <PackageDesc>{t(`套餐.${curWorkingPackage.id}`)}</PackageDesc>
      </PanelTitle>
      <PackageSelectPanel
        value={checkedPackageId}
        isEnterprise={isEnterprise}
        recommendPackageId={orderType === 'UPGRADE' ? undefined : recommendPackageId}
        list={packageData}
        disabled={(_, item) => {
          if (orderType === 'UPGRADE' && item) {
            const itemIndex = packageData.findIndex((v) => v.id === item.id);
            const workingPackageIndex = packageData.findIndex((v) => v.id === curWorkingPackage.id);
            return itemIndex <= workingPackageIndex;
          }
          return false;
        }}
        onChange={(isEnterprise, selected) => {
          setIsEnterprise(isEnterprise);
          if (isEnterprise) {
            // 企业版不支持自动续费
            setIsSubscription?.(false);
            onPackageChange?.(undefined);
            setDurationDesc([]);
          } else if (selected) {
            const { accountNum, id, durationDesc } = selected;
            setAccountNum(accountNum);
            onPackageChange?.(id);
            setDurationDesc(
              injectSubscriptionItem(
                orderType === 'UPGRADE' ? [noAddTime, ...durationDesc] : durationDesc,
              ),
            );
            if (orderType === 'UPGRADE') {
              if (isWorkingSubscription) {
                // 订阅用户升级：时长固定为连续包月，保持订阅
                setChkDuration(SUBSCRIPTION_DURATION_ID);
                setIsSubscription?.(true);
              } else {
                setChkDuration(0);
                setIsSubscription?.(false);
              }
            } else if (currency === 'CNY') {
              // 续订且 CNY：默认选中连续包月
              setChkDuration(SUBSCRIPTION_DURATION_ID);
              setIsSubscription?.(true);
            } else {
              setChkDuration(recommendDurationId);
              setIsSubscription?.(false);
            }
          }
        }}
      />
      {/* 订阅用户升级只选目标套餐，不显示时长选项；其余场景正常显示 */}
      {!hiddenDuration && durationDesc.length ? (
        <DurationSelectPanel
          durationId={chkDuration}
          packageId={checkedPackageId}
          orderType={orderType}
          currency={currency}
          packageData={packageData}
          recommendDurationId={orderType === 'UPGRADE' ? undefined : recommendDurationId}
          canIncreaseDuration={!curRenewPackage}
          list={durationDesc}
          disabled={(value) => !isUndefined(curRenewPackage) && value.id > 0}
          onChange={(value) => {
            setChkDuration(value.id);
            setIsSubscription?.(!!value.subscription);
          }}
        />
      ) : null}
    </Space>
  ) : null;
}

export default PackageDlgDetail;

export const Detail = styled.section`
  margin-bottom: 24px;
  h4 {
    display: flex;
    align-items: center;
    height: 20px;
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: normal;
    color: var(--fett-text-primary);
    span.packtip {
      margin-left: 8px;
      font-size: 12px;
      color: var(--fett-surface-warning-text);
      background: #faedd2;
      padding: 0 4px;
      border-radius: 2px;
    }
  }
`;
const PackageDesc = styled.span`
  color: var(--fett-text-secondary);
  font-weight: 400;
  margin-left: 8px;
`;
